import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BookOpen, Settings, FileText, ClipboardList, Save, ArrowLeft,
  Plus, Trash2, ChevronDown, ChevronUp, ExternalLink, Zap,
  CheckCircle, X, RefreshCw, AlertCircle, Tag, GripVertical,
  AlignLeft, BarChart2, Layers, HelpCircle, Video, ChevronLeft,
  ChevronRight, Code2, Repeat2, Milestone,
} from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Layout from '../components/Layout';
import ChatPanel from '../components/ChatPanel';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MODULE_TYPES = [
  { id: 'standard',   label: 'Standard',    color: '#94a3b8' },
  { id: 'intro',      label: 'Introduction', color: '#20c997' },
  { id: 'practice',   label: 'Pratique',     color: '#ffd93d' },
  { id: 'assessment', label: 'Évaluation',   color: '#ff6b6b' },
  { id: 'summary',    label: 'Résumé',       color: '#a78bfa' },
];

const BLOCK_TYPES = [
  { type: 'text',      label: 'Texte',       icon: AlignLeft,  color: '#94a3b8' },
  { type: 'stats',     label: 'Statistiques',icon: BarChart2,  color: '#20c997' },
  { type: 'schema',    label: 'Schéma',      icon: Layers,     color: '#a78bfa' },
  { type: 'quiz',      label: 'Quiz',        icon: HelpCircle, color: '#ffd93d' },
  { type: 'video',     label: 'Vidéo',       icon: Video,      color: '#ff6b6b' },
  { type: 'code',      label: 'Code',        icon: Code2,      color: '#e879f9' },
  { type: 'flashcard', label: 'Flashcard',   icon: Repeat2,    color: '#4d9eff' },
  { type: 'timeline',  label: 'Chronologie', icon: Milestone,  color: '#f97316' },
];

function getBlockMeta(type) {
  return BLOCK_TYPES.find(b => b.type === type) || BLOCK_TYPES[0];
}

let _blockIdCounter = Date.now();
function newBlockId() {
  return `block_${++_blockIdCounter}`;
}

// ── Auto-migration lesson → blocks ────────────────────────────────────────────
function migrateModuleToBlocks(mod) {
  if (mod.blocks && mod.blocks.length > 0) {
    return {
      ...mod,
      blocks: mod.blocks.map((b, i) => ({
        id: b.id || `block_${mod.id || 'x'}_${i}`,
        type: b.type || 'text',
        content: b.content ?? '',
        credits_cost: b.credits_cost || 0,
      })),
    };
  }

  const lesson = mod.lesson;
  const blocks = [];

  if (!lesson) {
    return { ...mod, blocks: [{ id: newBlockId(), type: 'text', content: '', credits_cost: 0 }] };
  }

  if (typeof lesson === 'string') {
    return { ...mod, blocks: [{ id: newBlockId(), type: 'text', content: lesson, credits_cost: 0 }] };
  }

  // key_points → text block
  if (lesson.key_points && lesson.key_points.length > 0) {
    blocks.push({
      id: newBlockId(), type: 'text', credits_cost: 0,
      content: `## Points clés\n\n${lesson.key_points.map(kp => `- ${kp}`).join('\n')}`,
    });
  }

  // stats
  if (lesson.stats && lesson.stats.length > 0) {
    blocks.push({
      id: newBlockId(), type: 'stats', credits_cost: 0,
      content: { title: 'Chiffres clés', items: lesson.stats },
    });
  }

  // schema
  if (lesson.schema && lesson.schema.elements && lesson.schema.elements.length > 0) {
    blocks.push({
      id: newBlockId(), type: 'schema', credits_cost: 0,
      content: lesson.schema,
    });
  }

  // quiz
  if (lesson.quiz && lesson.quiz.length > 0) {
    blocks.push({
      id: newBlockId(), type: 'quiz', credits_cost: 0,
      content: { questions: lesson.quiz },
    });
  }

  if (blocks.length === 0) {
    blocks.push({ id: newBlockId(), type: 'text', content: '', credits_cost: 0 });
  }

  return { ...mod, blocks };
}

// ── Block Renderers ───────────────────────────────────────────────────────────

function TextBlock({ block, onChange }) {
  return (
    <textarea
      value={typeof block.content === 'string' ? block.content : ''}
      onChange={e => onChange({ ...block, content: e.target.value })}
      className="w-full bg-[#0a0a10] border border-[#2a2935] text-[#e8e8e8] text-xs rounded-lg px-3 py-2.5 outline-none focus:border-[#20c997]/30 transition-colors resize-none min-h-[80px] font-mono"
      placeholder="Contenu en markdown... (ex: ## Titre, - point clé)"
      data-testid="text-block-editor"
    />
  );
}

function StatsBlock({ block }) {
  const items = block.content?.items || [];
  return (
    <div className="space-y-1">
      {items.length === 0 ? (
        <p className="text-xs text-[#64748b] italic">Aucune statistique</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((item, i) => (
            <div key={i} className="bg-[#20c997]/10 border border-[#20c997]/20 rounded-lg px-3 py-1.5 text-center">
              <p className="text-[#20c997] text-sm font-bold">{item.value}<span className="text-[10px]">{item.unit}</span></p>
              <p className="text-[#64748b] text-[10px]">{item.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SchemaBlock({ block }) {
  const content = block.content || {};
  const elements = content.elements || [];
  const COLOR_MAP = {
    coral: 'text-[#ff6b6b] bg-[#ff6b6b]/10 border-[#ff6b6b]/20',
    teal:  'text-[#20c997] bg-[#20c997]/10 border-[#20c997]/20',
    amber: 'text-[#ffc107] bg-[#ffc107]/10 border-[#ffc107]/20',
  };
  return (
    <div>
      {content.title && <p className="text-white text-xs font-semibold mb-1">{content.title}</p>}
      <div className="grid grid-cols-2 gap-1.5">
        {elements.map((el, i) => (
          <div key={i} className={`rounded-lg px-2 py-1.5 border text-[10px] ${COLOR_MAP[el.color] || COLOR_MAP.coral}`}>
            <span className="font-bold">{el.label}</span>
            {el.description && <p className="opacity-70 mt-0.5 leading-tight">{el.description.slice(0, 60)}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function QuizBlock({ block }) {
  const questions = block.content?.questions || [];
  return (
    <div className="space-y-1.5">
      {questions.length === 0 ? (
        <p className="text-xs text-[#64748b] italic">Aucune question</p>
      ) : questions.map((q, i) => (
        <div key={i} className="bg-[#ffd93d]/5 border border-[#ffd93d]/20 rounded-lg px-3 py-2">
          <p className="text-white text-xs font-medium">{i + 1}. {q.question?.slice(0, 80)}{q.question?.length > 80 ? '…' : ''}</p>
          <p className="text-[#64748b] text-[10px] mt-0.5">{q.options?.length || 0} options · Bonne réponse: n°{(q.correct_index ?? 0) + 1}</p>
        </div>
      ))}
    </div>
  );
}

function VideoBlock({ block, onChange }) {
  const content = block.content || {};
  return (
    <div className="space-y-2">
      <input
        value={content.url || ''}
        onChange={e => onChange({ ...block, content: { ...content, url: e.target.value } })}
        className="w-full bg-[#0a0a10] border border-[#2a2935] text-white text-xs rounded-lg px-3 py-2 outline-none focus:border-[#ff6b6b]/30"
        placeholder="URL de la vidéo (YouTube, Vimeo...)"
      />
      <input
        value={content.title || ''}
        onChange={e => onChange({ ...block, content: { ...content, title: e.target.value } })}
        className="w-full bg-[#0a0a10] border border-[#2a2935] text-white text-xs rounded-lg px-3 py-2 outline-none focus:border-[#ff6b6b]/30"
        placeholder="Titre de la vidéo"
      />
    </div>
  );
}

function CodeBlock({ block, onChange }) {
  const content = block.content || {};
  const LANGUAGES = ['javascript','python','typescript','html','css','bash','sql','json','yaml','java','php','rust','go','cpp'];
  return (
    <div className="space-y-2">
      <select
        value={content.language || 'javascript'}
        onChange={e => onChange({ ...block, content: { ...content, language: e.target.value } })}
        className="bg-[#0a0a10] border border-[#2a2935] text-[#94a3b8] text-xs rounded-lg px-2 py-2 outline-none focus:border-[#e879f9]/30"
        data-testid="code-language-select"
      >
        {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
      </select>
      <textarea
        value={content.code || ''}
        onChange={e => onChange({ ...block, content: { ...content, code: e.target.value } })}
        className="w-full bg-[#0a0a10] border border-[#2a2935] text-[#e8e8e8] text-xs rounded-lg px-3 py-2.5 outline-none focus:border-[#e879f9]/30 transition-colors resize-none min-h-[100px] font-mono"
        placeholder={`// Votre code ${content.language || 'javascript'} ici...`}
        data-testid="code-block-editor"
      />
    </div>
  );
}

function FlashcardBlock({ block, onChange }) {
  const content = block.content || {};
  return (
    <div className="space-y-2">
      <div>
        <label className="text-[10px] text-[#64748b] mb-1 block font-semibold uppercase">Recto — Question</label>
        <textarea
          value={content.front || ''}
          onChange={e => onChange({ ...block, content: { ...content, front: e.target.value } })}
          className="w-full bg-[#0a0a10] border border-[#2a2935] text-white text-xs rounded-lg px-3 py-2.5 outline-none focus:border-[#4d9eff]/30 transition-colors resize-none min-h-[60px]"
          placeholder="Question ou terme à mémoriser..."
          data-testid="flashcard-front-editor"
        />
      </div>
      <div>
        <label className="text-[10px] text-[#64748b] mb-1 block font-semibold uppercase">Verso — Réponse</label>
        <textarea
          value={content.back || ''}
          onChange={e => onChange({ ...block, content: { ...content, back: e.target.value } })}
          className="w-full bg-[#0a0a10] border border-[#2a2935] text-white text-xs rounded-lg px-3 py-2.5 outline-none focus:border-[#4d9eff]/30 transition-colors resize-none min-h-[60px]"
          placeholder="Définition ou réponse..."
          data-testid="flashcard-back-editor"
        />
      </div>
    </div>
  );
}

function TimelineBlock({ block, onChange }) {
  const content = block.content || {};
  const steps = content.steps || [];

  const updateStep = (i, field, value) => {
    const updated = steps.map((s, si) => si === i ? { ...s, [field]: value } : s);
    onChange({ ...block, content: { ...content, steps: updated } });
  };

  const addStep = () => {
    onChange({ ...block, content: { ...content, steps: [...steps, { label: '', title: '', description: '' }] } });
  };

  const removeStep = (i) => {
    onChange({ ...block, content: { ...content, steps: steps.filter((_, si) => si !== i) } });
  };

  return (
    <div className="space-y-2">
      {steps.map((step, i) => (
        <div key={i} className="bg-[#0a0a10] border border-[#f97316]/20 rounded-lg p-2.5 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-[#f97316] font-bold">Étape {i + 1}</span>
            <button onClick={() => removeStep(i)} className="text-[#64748b] hover:text-[#ff6b6b] p-0.5">
              <X size={10} />
            </button>
          </div>
          <input
            value={step.label || ''}
            onChange={e => updateStep(i, 'label', e.target.value)}
            placeholder="Étiquette (ex: Phase 1, 2021...)"
            className="w-full bg-[#16161e] border border-[#2a2935] text-white text-xs rounded px-2 py-1.5 outline-none"
          />
          <input
            value={step.title || ''}
            onChange={e => updateStep(i, 'title', e.target.value)}
            placeholder="Titre de l'étape"
            className="w-full bg-[#16161e] border border-[#2a2935] text-white text-xs rounded px-2 py-1.5 outline-none"
            data-testid={`timeline-step-title-${i}`}
          />
          <textarea
            value={step.description || ''}
            onChange={e => updateStep(i, 'description', e.target.value)}
            placeholder="Description (optionnelle)"
            rows={2}
            className="w-full bg-[#16161e] border border-[#2a2935] text-[#94a3b8] text-xs rounded px-2 py-1.5 outline-none resize-none"
          />
        </div>
      ))}
      <button
        onClick={addStep}
        className="flex items-center gap-1.5 text-xs text-[#64748b] hover:text-[#f97316] transition-colors"
        data-testid="add-timeline-step-btn"
      >
        <Plus size={11} /> Ajouter une étape
      </button>
    </div>
  );
}

// ── Single Block Card ─────────────────────────────────────────────────────────
function BlockCard({ block, index, total, onUpdate, onDelete, onMoveUp, onMoveDown }) {
  const meta = getBlockMeta(block.type);
  const Icon = meta.icon;
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="bg-[#0f0e17] border border-[#2a2935] rounded-xl overflow-hidden"
      data-testid={`block-card-${index}`}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <div
          className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${meta.color}15`, border: `1px solid ${meta.color}30` }}
        >
          <Icon size={11} style={{ color: meta.color }} />
        </div>
        <span className="text-[11px] font-semibold text-[#94a3b8] flex-1">{meta.label}</span>
        <div className="flex items-center gap-1">
          {index > 0 && (
            <button onClick={onMoveUp} className="p-1 text-[#64748b] hover:text-white transition-colors">
              <ChevronLeft size={11} />
            </button>
          )}
          {index < total - 1 && (
            <button onClick={onMoveDown} className="p-1 text-[#64748b] hover:text-white transition-colors">
              <ChevronRight size={11} />
            </button>
          )}
          <button onClick={() => setExpanded(p => !p)} className="p-1 text-[#64748b] hover:text-white transition-colors">
            {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
          <button onClick={onDelete} className="p-1 text-[#64748b] hover:text-[#ff6b6b] transition-colors">
            <X size={11} />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 border-t border-[#2a2935] pt-2">
          {block.type === 'text'      && <TextBlock      block={block} onChange={onUpdate} />}
          {block.type === 'stats'     && <StatsBlock     block={block} />}
          {block.type === 'schema'    && <SchemaBlock    block={block} />}
          {block.type === 'quiz'      && <QuizBlock      block={block} />}
          {block.type === 'video'     && <VideoBlock     block={block} onChange={onUpdate} />}
          {block.type === 'code'      && <CodeBlock      block={block} onChange={onUpdate} />}
          {block.type === 'flashcard' && <FlashcardBlock block={block} onChange={onUpdate} />}
          {block.type === 'timeline'  && <TimelineBlock  block={block} onChange={onUpdate} />}
          {block.type === 'stats' && (
            <p className="text-[10px] text-[#64748b] mt-2 italic">Les statistiques sont générées par IA. Modifiez via la régénération du cours.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Add Block Menu ────────────────────────────────────────────────────────────
function AddBlockMenu({ onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(p => !p)}
        className="w-full py-2 border border-dashed border-[#2a2935] rounded-xl text-[#64748b] text-xs hover:border-[#20c997]/30 hover:text-[#20c997] transition-all flex items-center justify-center gap-1.5"
        data-testid="add-block-menu-btn"
      >
        <Plus size={11} /> Ajouter un bloc
      </button>
      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-[#16161e] border border-[#2a2935] rounded-xl overflow-hidden z-20 shadow-xl">
          {BLOCK_TYPES.map(bt => {
            const BIcon = bt.icon;
            return (
              <button
                key={bt.type}
                onClick={() => { onAdd(bt.type); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[#94a3b8] hover:bg-[#20202a] hover:text-white transition-colors"
                data-testid={`add-block-${bt.type}`}
              >
                <BIcon size={13} style={{ color: bt.color }} />
                <span className="text-xs">{bt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Sortable Module ───────────────────────────────────────────────────────────
function SortableModule({ id, children, isDragging }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ listeners, attributes })}
    </div>
  );
}

// ── ContentTab ────────────────────────────────────────────────────────────────
function ContentTab({ modules, onChange, course, onSave }) {
  const [expanded, setExpanded] = useState({});
  const [draggingId, setDraggingId] = useState(null);
  const [enrichingModule, setEnrichingModule] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const updateModule = (idx, field, value) =>
    onChange(modules.map((m, i) => i === idx ? { ...m, [field]: value } : m));

  const updateBlock = (modIdx, blockIdx, updatedBlock) => {
    const updated = modules.map((m, mi) => {
      if (mi !== modIdx) return m;
      return { ...m, blocks: m.blocks.map((b, bi) => bi === blockIdx ? updatedBlock : b) };
    });
    onChange(updated);
  };

  const deleteBlock = (modIdx, blockIdx) => {
    const updated = modules.map((m, mi) => {
      if (mi !== modIdx) return m;
      const newBlocks = m.blocks.filter((_, bi) => bi !== blockIdx);
      return { ...m, blocks: newBlocks.length > 0 ? newBlocks : [{ id: newBlockId(), type: 'text', content: '', credits_cost: 0 }] };
    });
    onChange(updated);
  };

  const moveBlock = (modIdx, blockIdx, direction) => {
    const updated = modules.map((m, mi) => {
      if (mi !== modIdx) return m;
      const newBlocks = [...m.blocks];
      const target = blockIdx + direction;
      if (target < 0 || target >= newBlocks.length) return m;
      [newBlocks[blockIdx], newBlocks[target]] = [newBlocks[target], newBlocks[blockIdx]];
      return { ...m, blocks: newBlocks };
    });
    onChange(updated);
  };

  const addBlock = (modIdx, type) => {
    const defaults = {
      text:      '',
      stats:     { title: 'Chiffres clés', items: [{ label: 'Statistique', value: '0', unit: '' }] },
      schema:    { title: 'Schéma', description: '', elements: [] },
      quiz:      { questions: [] },
      video:     { url: '', title: '' },
      code:      { language: 'javascript', code: '// Votre code ici' },
      flashcard: { front: 'Question ou terme à mémoriser', back: 'Réponse ou définition' },
      timeline:  { steps: [{ label: 'Étape 1', title: '', description: '' }] },
    };
    const updated = modules.map((m, mi) => {
      if (mi !== modIdx) return m;
      return { ...m, blocks: [...(m.blocks || []), { id: newBlockId(), type, content: defaults[type] ?? '', credits_cost: 0 }] };
    });
    onChange(updated);
  };

  const addModule = () => {
    const newMod = {
      id: `module_${Date.now()}`,
      title: 'Nouveau module',
      description: '',
      type: 'standard',
      image_url: '',
      blocks: [{ id: newBlockId(), type: 'text', content: '', credits_cost: 0 }],
    };
    onChange([...modules, newMod]);
  };

  const handleEnrichModule = async (idx) => {
    if (!course?.id) return;
    setEnrichingModule(idx);
    try {
      const res = await axios.post(
        `${API}/courses/${course.id}/enrich-module`,
        { module_index: idx },
        { withCredentials: true }
      );
      const newBlocks = (res.data.blocks || []).map((b, i) => ({
        ...b,
        id: b.id || `block_${Date.now()}_${i}`,
        credits_cost: 0,
      }));
      const updated = modules.map((m, i) => i === idx ? { ...m, blocks: newBlocks } : m);
      onChange(updated);
      // Auto-save after enrichment
      try {
        await axios.patch(`${API}/courses/${course.id}`, { modules: updated }, { withCredentials: true });
        toast.success(`Module enrichi (${res.data.credits_used} cr)`);
      } catch {
        toast.success('Module enrichi — pensez à sauvegarder');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur d\'enrichissement';
      toast.error(detail);
    } finally {
      setEnrichingModule(null);
    }
  };

  const removeModule = (idx) => {
    if (!window.confirm('Supprimer ce module définitivement ?')) return;
    onChange(modules.filter((_, i) => i !== idx));
  };

  const handleDragStart = (event) => setDraggingId(event.active.id);
  const handleDragEnd = (event) => {
    setDraggingId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = modules.findIndex(m => m.id === active.id);
    const newIdx = modules.findIndex(m => m.id === over.id);
    if (oldIdx !== -1 && newIdx !== -1) {
      onChange(arrayMove(modules, oldIdx, newIdx));
    }
  };

  const moduleIds = modules.map(m => m.id).filter(Boolean);

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-bold px-1">
        Glissez les modules pour les réordonner
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={moduleIds} strategy={verticalListSortingStrategy}>
          {modules.map((mod, idx) => {
            const typeConfig = MODULE_TYPES.find(t => t.id === mod.type) || MODULE_TYPES[0];
            const isExp = expanded[mod.id || idx];
            const blocksCount = mod.blocks?.length || 0;
            const domId = mod.id || `mod_${idx}`;

            return (
              <SortableModule key={domId} id={domId} isDragging={draggingId === domId}>
                {({ listeners, attributes }) => (
                  <div
                    className="bg-[#0f0e17] border border-[#2a2935] rounded-xl overflow-hidden"
                    data-testid={`module-item-${idx}`}
                  >
                    <div className="flex items-center gap-3 px-4 py-3">
                      {/* Drag handle */}
                      <button
                        {...listeners}
                        {...attributes}
                        className="p-1 text-[#2a2935] hover:text-[#64748b] transition-colors cursor-grab active:cursor-grabbing shrink-0"
                        data-testid={`module-drag-${idx}`}
                      >
                        <GripVertical size={14} />
                      </button>

                      {/* Index */}
                      <div className="w-6 h-6 rounded-lg bg-[#16161e] border border-[#2a2935] flex items-center justify-center shrink-0 text-[10px] font-bold text-[#64748b]">
                        {idx + 1}
                      </div>

                      {/* Title */}
                      <div className="flex-1 min-w-0">
                        <input
                          value={mod.title}
                          onChange={e => updateModule(idx, 'title', e.target.value)}
                          className="w-full bg-transparent text-white text-sm font-medium outline-none border-b border-transparent focus:border-[#20c997]/40 transition-colors"
                          placeholder="Titre du module"
                          data-testid={`module-title-${idx}`}
                        />
                        <span className="text-[10px] text-[#64748b]">{blocksCount} bloc{blocksCount > 1 ? 's' : ''}</span>
                      </div>

                      {/* Type */}
                      <select
                        value={mod.type || 'standard'}
                        onChange={e => updateModule(idx, 'type', e.target.value)}
                        className="text-[10px] font-bold border rounded-lg px-2 py-1 outline-none cursor-pointer bg-[#16161e] border-[#2a2935] transition-colors hidden sm:block"
                        style={{ color: typeConfig.color, borderColor: `${typeConfig.color}30` }}
                        data-testid={`module-type-${idx}`}
                      >
                        {MODULE_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>

                      <button
                        onClick={() => toggleExpand(mod.id || idx)}
                        className="p-1.5 text-[#64748b] hover:text-white transition-colors"
                        data-testid={`module-expand-${idx}`}
                      >
                        {isExp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      <button
                        onClick={() => handleEnrichModule(idx)}
                        disabled={enrichingModule !== null}
                        title="Enrichir ce module avec l'IA (2 cr)"
                        className="p-1.5 text-[#64748b] hover:text-[#20c997] transition-colors disabled:opacity-40"
                        data-testid={`enrich-module-${idx}`}
                      >
                        {enrichingModule === idx
                          ? <RefreshCw size={13} className="animate-spin text-[#20c997]" />
                          : <Zap size={13} />}
                      </button>
                      <button
                        onClick={() => removeModule(idx)}
                        className="p-1.5 text-[#64748b] hover:text-[#ff6b6b] transition-colors"
                        data-testid={`remove-module-${idx}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>

                    {isExp && (
                      <div className="px-4 pb-4 border-t border-[#2a2935] pt-3 space-y-3">
                        {/* Description */}
                        <textarea
                          value={mod.description || ''}
                          onChange={e => updateModule(idx, 'description', e.target.value)}
                          className="w-full bg-[#16161e] border border-[#2a2935] text-[#e8e8e8] text-xs rounded-xl px-3 py-2.5 outline-none focus:border-[#20c997]/40 transition-colors resize-none min-h-[60px]"
                          placeholder="Description du module..."
                          data-testid={`module-desc-${idx}`}
                        />

                        {/* Blocks */}
                        {(mod.blocks || []).length > 0 && (
                          <div className="space-y-2">
                            <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-bold">Blocs de contenu</p>
                            {(mod.blocks || []).map((block, bi) => (
                              <BlockCard
                                key={block.id || bi}
                                block={block}
                                index={bi}
                                total={mod.blocks.length}
                                onUpdate={updatedBlock => updateBlock(idx, bi, updatedBlock)}
                                onDelete={() => deleteBlock(idx, bi)}
                                onMoveUp={() => moveBlock(idx, bi, -1)}
                                onMoveDown={() => moveBlock(idx, bi, 1)}
                              />
                            ))}
                          </div>
                        )}

                        <AddBlockMenu onAdd={(type) => addBlock(idx, type)} />
                      </div>
                    )}
                  </div>
                )}
              </SortableModule>
            );
          })}
        </SortableContext>
      </DndContext>

      <button
        onClick={addModule}
        className="w-full py-3 border-2 border-dashed border-[#2a2935] rounded-xl text-[#64748b] text-sm hover:border-[#20c997]/30 hover:text-[#20c997] transition-all flex items-center justify-center gap-2"
        data-testid="add-module-btn"
      >
        <Plus size={15} /> Ajouter un module
      </button>
    </div>
  );
}

// ── ResourcesTab ──────────────────────────────────────────────────────────────
function ResourcesTab({ modules, onChange }) {
  const getResources = (mod) => mod.resources || mod.lesson?.resources || [];

  const updateResource = (modIdx, resIdx, field, value) => {
    const updated = modules.map((m, i) => {
      if (i !== modIdx) return m;
      const resources = getResources(m).map((r, ri) =>
        ri === resIdx ? { ...r, [field]: value } : r
      );
      // Save at module level (new format)
      return { ...m, resources };
    });
    onChange(updated);
  };

  const addResource = (modIdx) => {
    const updated = modules.map((m, i) => {
      if (i !== modIdx) return m;
      return { ...m, resources: [...getResources(m), { title: '', url: '', type: 'article' }] };
    });
    onChange(updated);
  };

  const removeResource = (modIdx, resIdx) => {
    const updated = modules.map((m, i) => {
      if (i !== modIdx) return m;
      return { ...m, resources: getResources(m).filter((_, ri) => ri !== resIdx) };
    });
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {modules.map((mod, modIdx) => (
        <div key={mod.id || modIdx} className="bg-[#0f0e17] border border-[#2a2935] rounded-xl p-4">
          <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <span className="w-5 h-5 rounded-md bg-[#16161e] text-[10px] font-bold text-[#64748b] flex items-center justify-center">{modIdx + 1}</span>
            {mod.title}
          </p>
          {getResources(mod).length === 0 ? (
            <p className="text-xs text-[#64748b] italic mb-3">Aucune ressource</p>
          ) : (
            <div className="space-y-2 mb-3">
              {getResources(mod).map((res, resIdx) => (
                <div key={resIdx} className="flex gap-2 items-start" data-testid={`resource-${modIdx}-${resIdx}`}>
                  <input
                    value={res.title || ''}
                    onChange={e => updateResource(modIdx, resIdx, 'title', e.target.value)}
                    className="flex-1 bg-[#16161e] border border-[#2a2935] text-white text-xs rounded-lg px-3 py-2 outline-none focus:border-[#20c997]/30 transition-colors"
                    placeholder="Titre de la ressource"
                  />
                  <input
                    value={res.url || ''}
                    onChange={e => updateResource(modIdx, resIdx, 'url', e.target.value)}
                    className="flex-1 bg-[#16161e] border border-[#2a2935] text-white text-xs rounded-lg px-3 py-2 outline-none focus:border-[#20c997]/30 transition-colors"
                    placeholder="https://..."
                  />
                  {res.url && (
                    <a href={res.url} target="_blank" rel="noopener noreferrer" className="p-2 text-[#64748b] hover:text-[#20c997] transition-colors">
                      <ExternalLink size={13} />
                    </a>
                  )}
                  <button onClick={() => removeResource(modIdx, resIdx)} className="p-2 text-[#64748b] hover:text-[#ff6b6b] transition-colors">
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={() => addResource(modIdx)}
            className="text-xs text-[#64748b] hover:text-[#20c997] transition-colors flex items-center gap-1"
            data-testid={`add-resource-${modIdx}`}
          >
            <Plus size={12} /> Ajouter une ressource
          </button>
        </div>
      ))}
    </div>
  );
}

// ── QuizTab ───────────────────────────────────────────────────────────────────
function QuizTab({ course, modules }) {
  const [selectedMod, setSelectedMod] = useState(0);
  const [quiz, setQuiz] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState({});

  const handleGenerateQuiz = async () => {
    const mod = modules[selectedMod];
    if (!mod) return;
    setGenerating(true);
    setSelectedAnswer({});
    try {
      const res = await axios.post(`${API}/chat/generate-quiz`, {
        course_id: course.id,
        module_title: mod.title,
        module_description: mod.description || '',
        num_questions: 5,
      }, { withCredentials: true });
      setQuiz(res.data.questions || []);
      toast.success(`Quiz généré (${res.data.credits_used} cr)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de génération');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex gap-2 items-center">
        <select
          value={selectedMod}
          onChange={e => { setSelectedMod(+e.target.value); setQuiz([]); setSelectedAnswer({}); }}
          className="flex-1 bg-[#0f0e17] border border-[#2a2935] text-white text-sm rounded-xl px-3 py-2.5 outline-none focus:border-[#20c997]/30"
          data-testid="quiz-module-select"
        >
          {modules.map((m, i) => <option key={i} value={i}>{m.title}</option>)}
        </select>
        <button
          onClick={handleGenerateQuiz}
          disabled={generating}
          className="flex items-center gap-2 bg-[#ffd93d] text-[#0f0e17] px-4 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
          data-testid="generate-quiz-btn"
        >
          {generating ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
          Générer quiz IA
          <span className="text-[10px] opacity-70">(2 cr)</span>
        </button>
      </div>
      {quiz.length > 0 && (
        <div className="space-y-4" data-testid="quiz-questions">
          {quiz.map((q, qi) => {
            const userAnswer = selectedAnswer[qi];
            return (
              <div key={qi} className="bg-[#0f0e17] border border-[#2a2935] rounded-xl p-4">
                <p className="text-sm font-semibold text-white mb-3">
                  <span className="text-[#64748b] mr-2">Q{qi + 1}.</span> {q.question}
                </p>
                <div className="space-y-2">
                  {(q.options || []).map((opt, oi) => {
                    const letter = opt.charAt(0);
                    const isCorrect = letter === q.correct;
                    const isSelected = userAnswer === letter;
                    const showResult = userAnswer !== undefined;
                    return (
                      <button
                        key={oi}
                        onClick={() => setSelectedAnswer(prev => ({ ...prev, [qi]: letter }))}
                        className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all ${
                          showResult
                            ? isCorrect ? 'bg-[#20c997]/10 border border-[#20c997]/30 text-[#20c997]'
                              : isSelected ? 'bg-[#ff6b6b]/10 border border-[#ff6b6b]/30 text-[#ff6b6b]'
                              : 'bg-[#16161e] border border-[#2a2935] text-[#64748b]'
                            : isSelected ? 'bg-[#20c997]/5 border border-[#20c997]/30 text-white'
                            : 'bg-[#16161e] border border-[#2a2935] text-[#e8e8e8] hover:border-[#3a3945]'
                        }`}
                      >
                        {opt}
                        {showResult && isCorrect && <CheckCircle size={12} className="inline ml-2" />}
                      </button>
                    );
                  })}
                </div>
                {userAnswer !== undefined && q.explanation && (
                  <p className="mt-3 text-xs text-[#94a3b8] bg-[#16161e] border border-[#2a2935] rounded-lg px-3 py-2">{q.explanation}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
      {quiz.length === 0 && !generating && (
        <div className="text-center py-10">
          <ClipboardList size={32} className="text-[#2a2935] mx-auto mb-3" />
          <p className="text-[#64748b] text-sm">Cliquez sur "Générer quiz IA" pour créer un quiz</p>
          <p className="text-[#64748b] text-xs mt-1 flex items-center justify-center gap-1">
            <Zap size={10} className="text-[#ffd93d]" /> Coût : 2 crédits
          </p>
        </div>
      )}
    </div>
  );
}

// ── SettingsTab ───────────────────────────────────────────────────────────────
function SettingsTab({ course, onSave, referentials }) {
  const [form, setForm] = useState({
    title: course.title || '',
    description: course.description || '',
    estimated_hours: course.estimated_hours || 2,
    language: course.language || 'fr',
    targeted_referentials: course.targeted_referentials || [],
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="space-y-5">
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-[#94a3b8] uppercase tracking-wide mb-1.5">Titre</label>
          <input
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            className="w-full bg-[#0f0e17] border border-[#2a2935] text-white rounded-xl px-4 py-3 text-sm outline-none focus:border-[#20c997]/40 transition-colors"
            data-testid="settings-title-input"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-[#94a3b8] uppercase tracking-wide mb-1.5">Description</label>
          <textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            className="w-full bg-[#0f0e17] border border-[#2a2935] text-white rounded-xl px-4 py-3 text-sm outline-none focus:border-[#20c997]/40 transition-colors resize-none min-h-[100px]"
            data-testid="settings-desc-input"
          />
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-xs font-bold text-[#94a3b8] uppercase tracking-wide mb-1.5">Durée estimée (h)</label>
            <input
              type="number" min="0.5" max="100" step="0.5"
              value={form.estimated_hours}
              onChange={e => setForm(f => ({ ...f, estimated_hours: parseFloat(e.target.value) || 1 }))}
              className="w-full bg-[#0f0e17] border border-[#2a2935] text-white rounded-xl px-4 py-3 text-sm outline-none focus:border-[#20c997]/40"
              data-testid="settings-hours-input"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-bold text-[#94a3b8] uppercase tracking-wide mb-1.5">Langue</label>
            <select
              value={form.language}
              onChange={e => setForm(f => ({ ...f, language: e.target.value }))}
              className="w-full bg-[#0f0e17] border border-[#2a2935] text-white rounded-xl px-4 py-3 text-sm outline-none focus:border-[#20c997]/40"
              data-testid="settings-lang-select"
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
        {referentials.length > 0 && (
          <div>
            <label className="block text-xs font-bold text-[#94a3b8] uppercase tracking-wide mb-1.5">Référentiels ciblés</label>
            <div className="flex flex-wrap gap-2">
              {referentials.slice(0, 8).map(ref => {
                const isSelected = form.targeted_referentials?.includes(ref.name);
                return (
                  <button
                    key={ref.name}
                    onClick={() => {
                      const refs = form.targeted_referentials || [];
                      setForm(f => ({
                        ...f,
                        targeted_referentials: isSelected
                          ? refs.filter(r => r !== ref.name)
                          : [...refs, ref.name],
                      }));
                    }}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all ${isSelected ? 'bg-[#a78bfa]/10 border-[#a78bfa]/40 text-[#a78bfa]' : 'bg-[#0f0e17] border-[#2a2935] text-[#64748b] hover:text-white hover:border-[#3a3945]'}`}
                    data-testid={`ref-toggle-${ref.name}`}
                  >
                    {isSelected && <Tag size={9} className="inline mr-1" />}
                    {ref.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 bg-[#20c997] text-[#0f0e17] px-5 py-2.5 rounded-xl text-sm font-bold hover:opacity-90 disabled:opacity-50 transition-opacity"
        data-testid="settings-save-btn"
      >
        {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
        Enregistrer
      </button>
    </div>
  );
}

// ── Main CourseEditor ─────────────────────────────────────────────────────────
const TABS = [
  { id: 'content',   label: 'Contenu',    icon: BookOpen },
  { id: 'resources', label: 'Ressources', icon: ExternalLink },
  { id: 'quiz',      label: 'Quiz',       icon: ClipboardList },
  { id: 'settings',  label: 'Paramètres', icon: Settings },
];

export default function CourseEditor() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('content');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [referentials, setReferentials] = useState([]);
  const [migrationDone, setMigrationDone] = useState(false);

  useEffect(() => {
    loadCourse();
    loadReferentials();
  }, [courseId]);

  const loadCourse = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/courses/${courseId}`, { withCredentials: true });
      const courseData = res.data;
      setCourse(courseData);

      // Auto-migration : lesson → blocks dans l'état local
      const rawMods = courseData.modules || [];
      const migrated = rawMods.map(mod => migrateModuleToBlocks(mod));
      setModules(migrated);

      const hadLegacy = rawMods.some(m => !m.blocks || m.blocks.length === 0);
      if (hadLegacy) setMigrationDone(true);
    } catch {
      toast.error('Cours introuvable');
      navigate('/library');
    } finally {
      setLoading(false);
    }
  };

  const loadReferentials = async () => {
    try {
      const res = await axios.get(`${API}/referentials`, { withCredentials: true });
      setReferentials(res.data.referentials || []);
    } catch { /* ignore */ }
  };

  const handleModulesChange = useCallback((updated) => {
    setModules(updated);
    setDirty(true);
  }, []);

  const handleSaveContent = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/courses/${courseId}`, { modules }, { withCredentials: true });
      toast.success('Contenu sauvegardé');
      setDirty(false);
    } catch {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async (formData) => {
    try {
      const updated = await axios.patch(`${API}/courses/${courseId}`, formData, { withCredentials: true });
      setCourse(updated.data);
      toast.success('Paramètres enregistrés');
    } catch {
      toast.error('Erreur de sauvegarde');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="w-8 h-8 border-2 border-[#20c997] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!course) return null;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 pt-6 pb-8 space-y-5">

        {/* Migration notice */}
        {migrationDone && (
          <div className="flex items-start gap-2.5 bg-[#20c997]/5 border border-[#20c997]/20 rounded-xl px-4 py-3" data-testid="migration-notice">
            <CheckCircle size={14} className="text-[#20c997] shrink-0 mt-0.5" />
            <p className="text-xs text-[#20c997]">
              Modules migrés vers l'architecture blocs. Les blocs sont éditables dans cet espace — le contenu de lecture reste inchangé.
            </p>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(`/course/${courseId}`)}
              className="p-2 rounded-xl bg-[#16161e] border border-[#2a2935] text-[#64748b] hover:text-white transition-colors shrink-0"
              data-testid="back-to-course-btn"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="min-w-0">
              <h1 className="font-heading text-xl font-bold text-white truncate" data-testid="editor-course-title">
                {course.title}
              </h1>
              <p className="text-xs text-[#64748b]">{modules.length} modules</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => setChatOpen(true)}
              className="flex items-center gap-1.5 text-sm text-[#20c997] border border-[#20c997]/30 px-3 py-2 rounded-xl hover:bg-[#20c997]/10 transition-all"
              data-testid="editor-chat-btn"
            >
              <FileText size={13} /> Chat IA
            </button>
            {dirty && activeTab !== 'settings' && (
              <button
                onClick={handleSaveContent}
                disabled={saving}
                className="flex items-center gap-1.5 bg-[#20c997] text-[#0f0e17] text-sm font-bold px-4 py-2 rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
                data-testid="save-content-btn"
              >
                {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                Sauvegarder
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#16161e] border border-[#2a2935] rounded-xl p-1" data-testid="editor-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all justify-center ${
                  isActive ? 'bg-[#ff6b6b] text-white' : 'text-[#64748b] hover:text-white'
                }`}
              >
                <Icon size={14} />
                <span className="hidden sm:block">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div data-testid="editor-content">
          {activeTab === 'content'   && <ContentTab   modules={modules} onChange={handleModulesChange} course={course} onSave={handleSaveContent} />}
          {activeTab === 'resources' && <ResourcesTab modules={modules} onChange={handleModulesChange} />}
          {activeTab === 'quiz'      && <QuizTab course={course} modules={modules} />}
          {activeTab === 'settings'  && <SettingsTab course={course} onSave={handleSaveSettings} referentials={referentials} />}
        </div>

        <div className="text-center">
          <button
            onClick={() => navigate(`/course/${courseId}`)}
            className="text-xs text-[#64748b] hover:text-white transition-colors flex items-center gap-1 mx-auto"
          >
            <ExternalLink size={11} /> Prévisualiser le cours
          </button>
        </div>
      </div>

      <ChatPanel
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        courseId={courseId}
        courseTitle={course.title}
      />
    </Layout>
  );
}
